import { appAxios } from 'api/axios';
import { sendCatchFeedback } from 'functions/feedback';
import { useCallback, useEffect, useState, useRef } from 'react';
import { StateOptionResponse } from 'types';

type OptionType = {
  value: string;
  label: string;
};

const useFetchStates = (maxRetries: number = 5, baseDelay: number = 1000) => {
  const [data, setData] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);

  const fetchStates = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null); // reset error before fetching

        const response = await appAxios.get<StateOptionResponse>('/states', {
          signal,
        });

        if (response?.data?.success) {
          const options: OptionType[] = response.data.data.map((option) => ({
            value: option.id,
            label: option.name,
          }));
          setData(options);
          retryCountRef.current = 0; // ✅ reset retries on success
        } else {
          throw new Error('Failed to fetch states');
        }
      } catch (err: any) {
        if (err.name !== 'CanceledError') {
          setError('An error occurred while fetching states');
          sendCatchFeedback(err);

          // ✅ retry logic
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            const delay = baseDelay * retryCountRef.current; // exponential backoff
            setTimeout(() => {
              const controller = new AbortController();
              fetchStates(controller.signal);
            }, delay);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [maxRetries, baseDelay]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchStates(controller.signal);
    return () => controller.abort();
  }, [fetchStates]);

  // ✅ expose manual retry
  const refetch = () => {
    retryCountRef.current = 0;
    const controller = new AbortController();
    fetchStates(controller.signal);
    return () => controller.abort();
  };

  return { data, loading, error, refetch };
};

export default useFetchStates;
